import React from 'react';
import { Box, Button, CircularProgress, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AuditRecord, AuditRow, useAuditRecord } from './auditApi';
import {
  RESULT_STYLES, entityLabel, extraPairs, formatDateTime, hiddenCount, pharmacyLabel, readChanges, sectionPath,
  toPairs, userLabel, valueToText
} from './auditFormat';
import { BORDER, ChangesList, Field, LABEL_SX, Pairs, Section, VALUE_SX } from './AuditParts';

const RelatedList: React.FC<{ rows: AuditRow[]; onOpen: (id: number) => void }> = ({ rows, onOpen }) => (
  <Box sx={{ border: BORDER, borderRadius: '8px', overflow: 'hidden' }}>
    {rows.map((row, index) => (
      <Box
        key={row.id}
        onClick={() => onOpen(row.id)}
        sx={{
          display: 'flex', gap: 1.5, alignItems: 'baseline', px: 1.5, py: 0.9, cursor: 'pointer',
          borderBottom: index < rows.length - 1 ? '1px solid #f1f5f9' : 'none', '&:hover': { backgroundColor: '#f8fafc' }
        }}
      >
        <Typography sx={{ ...VALUE_SX, flex: 1, fontWeight: 600 }}>{row.actionTitle}</Typography>
        <Typography sx={{ ...LABEL_SX, whiteSpace: 'nowrap' }}>{entityLabel(row) || row.resultTitle}</Typography>
      </Box>
    ))}
  </Box>
);

const RecordBody: React.FC<{ record: AuditRecord; related: AuditRow[]; onOpenRelated: (id: number) => void }> = ({
  record, related, onOpenRelated
}) => {
  const details = record.details;
  const changes = readChanges(details);
  const snapshot = toPairs(details ? details.snapshot : null);
  const params = toPairs(details ? details.params : null);
  const extra = extraPairs(details);
  const ids = details && Array.isArray(details.ids) ? details.ids : [];
  const idsHidden = hiddenCount(ids.length, details ? details.total : undefined);
  const person = userLabel(record);
  const technical = [record.httpMethod, record.httpPath].filter(Boolean).join(' ');

  return (
    <>
      <Section title="Кто выполнил">
        <Field label="Пользователь" value={person} />
        <Field label="Роль" value={record.roleName ? `${record.roleName}${record.roleLevel !== null ? ` (ранг ${record.roleLevel})` : ''}` : null} />
        <Field label="Реальный исполнитель" value={record.actorUsername ? `${record.actorUsername} (имперсонация)` : null} />
        <Field label="IP-адрес" value={record.ip} />
        <Field label="Браузер" value={record.userAgent} />
      </Section>

      <Section title="Что сделано">
        <Field label="Раздел" value={sectionPath(record)} />
        <Field label="Вид действия" value={record.kindTitle} />
        <Field label="Объект" value={entityLabel(record)} />
        <Field label="Тип объекта" value={record.entityType} />
        <Field label="Аптека" value={pharmacyLabel(record)} />
        <Field label="Период" value={record.period} />
        <Field label="Сообщение" value={record.message} />
        <Field label="Кратко" value={record.summary} />
      </Section>

      {details && details.truncated && (
        <Section title="Подробности">
          <Typography sx={VALUE_SX}>{details.note || 'Данные слишком велики и сохранены не полностью'}</Typography>
          {details.total !== undefined && details.total !== null && <Field label="Всего записей" value={String(details.total)} />}
        </Section>
      )}

      {changes.length > 0 && (
        <Section title="Что изменилось">
          <ChangesList changes={changes} total={details ? details.changesTotal : undefined} />
        </Section>
      )}

      {snapshot.length > 0 && (
        <Section title="Данные записи на момент действия">
          <Pairs pairs={snapshot} />
        </Section>
      )}

      {ids.length > 0 && (
        <Section title={`Затронутые записи: ${details && details.total !== undefined ? details.total : ids.length}`}>
          <Typography sx={{ ...VALUE_SX, color: '#334155' }}>{ids.map((id) => valueToText(id)).join(', ')}</Typography>
          {idsHidden > 0 && <Typography sx={{ ...LABEL_SX, mt: 0.5 }}>Показаны первые {ids.length}, еще {idsHidden}</Typography>}
        </Section>
      )}

      {params.length > 0 && (
        <Section title="Параметры запроса">
          <Pairs pairs={params} />
        </Section>
      )}

      {extra.length > 0 && (
        <Section title="Дополнительно">
          <Pairs pairs={extra} />
        </Section>
      )}

      {related.length > 0 && (
        <Section title="Другие записи этого запроса">
          <RelatedList rows={related} onOpen={onOpenRelated} />
        </Section>
      )}

      <Section title="Служебные данные">
        <Field label="Номер записи" value={String(record.id)} />
        <Field label="Запрос" value={technical} />
        <Field label="ID запроса" value={record.requestId} />
        <Field label="Длительность" value={record.durationMs !== null && record.durationMs !== undefined ? `${record.durationMs} мс` : null} />
      </Section>
    </>
  );
};

interface Props {
  /** Номер открытой записи; null - панель закрыта */
  recordId: number | null;
  onClose: () => void;
  onOpenRelated: (id: number) => void;
}

/** Правая панель: полная запись журнала с изменениями «было → стало» */
const AuditDetailsDrawer: React.FC<Props> = ({ recordId, onClose, onOpenRelated }) => {
  const { data, loading, error, reload } = useAuditRecord(recordId);
  const result = data ? RESULT_STYLES[data.record.result] || RESULT_STYLES.error : null;

  return (
    <Drawer
      anchor="right"
      open={recordId !== null}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, display: 'flex', flexDirection: 'column' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 3, py: 2, backgroundColor: '#ffffff', borderBottom: BORDER }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}>
            {data ? data.record.actionTitle : 'Запись журнала'}
          </Typography>
          {data && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography sx={LABEL_SX}>{formatDateTime(data.record.createdAt)}</Typography>
              {result && (
                <Box
                  component="span"
                  sx={{
                    px: 1, py: 0.1, borderRadius: '6px', fontSize: '0.74rem', fontWeight: 700,
                    color: result.color, backgroundColor: result.background, border: `1px solid ${result.border}`
                  }}
                >
                  {data.record.resultTitle}
                </Box>
              )}
            </Box>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Закрыть" sx={{ color: '#64748b' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && error && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ color: '#b91c1c', fontSize: '0.9rem', mb: 1.5 }}>{error}</Typography>
            <Button onClick={reload} sx={{ textTransform: 'none', fontWeight: 600 }}>Повторить</Button>
          </Box>
        )}
        {!loading && data && <RecordBody record={data.record} related={data.related} onOpenRelated={onOpenRelated} />}
      </Box>
    </Drawer>
  );
};

export default AuditDetailsDrawer;
